import { useState } from 'react'
import { leases as leaseApi } from '@/api/endpoints'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { POWER, type VmAction, vmBatchActions } from '@/domain/lease'
import { ownerLabel } from '@/domain/owner'
import { useSession } from '@/hooks/useSession'
import type { LeaseRow } from '../model'
import { ACTION, ACTION_DANGER, ACTION_PLAIN } from './chips'

// 관리자 VM 목록 검색 칸 오른쪽의 일괄 조작 — 대상은 체크한 VM 들이다. 조작은 사용자 VM 상세의 것과 같고(전원 다섯과 반납),
// 칩은 고른 VM 모두가 그 조작을 허락할 때만 선다(`domain/lease.ts` 의 `vmBatchActions`).
// 실행만 묻지 않고 나머지는 대상의 목록을 보이고 묻는다 — 창은 연 순간의 대상에만 간다. 반납의 창에만 결과 한 문장이 있다.
// 한 건씩 차례로 부르고, 된 VM 은 고르기에서 빠지며(onSettled) 실패한 VM 만 골라진 채 남는다 — 같은 칩·버튼이 곧 재시도다.
// 첫 오류는 상태 하나이고 한 자리에만 선다 — 창이 열려 있으면 창 안, 닫혀 있으면 칩 아래 줄이다. 다시 읽은 목록에서
// 실패한 VM 이 그 조작을 허락하지 않게 되면 창은 저절로 닫히는데, 오류까지 거두면 골라진 채 남은 VM 을 아무도 설명하지 않는다.
// 칩의 낱말은 도는 동안에도 그대로다 — `실행 중` 은 VM 의 상태 낱말이라 진행을 그렇게 말하면 이미 실행된 것으로 읽힌다.
// 도는 동안(busy)은 부르는 쪽이 고르기를 잠그고 칩도 잠긴다 — 누른 순간의 목록을 끝까지 돌기 때문이다.

/** 한 번 돈 조작의 첫 오류와 실패한 VM 의 임대 id. 실패한 VM 이 하나도 골라져 있지 않으면 거둔다. */
type Failure = { error: unknown; keys: ReadonlySet<string> }

type Chip = { action: VmAction; word: string; confirm: boolean; danger: boolean }
const CHIPS: Chip[] = [
  ...POWER.map((p) => ({ action: p.action, word: p.word, confirm: p.confirm, danger: false })),
  { action: 'end', word: '반납', confirm: true, danger: true },
]

function call(row: LeaseRow, action: VmAction): Promise<unknown> {
  return action === 'end' ? leaseApi.end(row.lease.id) : leaseApi.power(row.lease.id, action)
}

const nameOf = (row: LeaseRow) => row.vm?.name ?? row.lease.name

/**
 * 칩과 묻는 창, 그리고 창 밖의 첫 오류. 부르는 쪽의 `flex-wrap` 줄 안에 놓인다 — 오류는 그 줄에서 한 줄을 다 쓴다.
 */
export function VmBatch({
  chosen,
  busy,
  onBusy,
  onSettled,
}: {
  chosen: LeaseRow[]
  busy: VmAction | null
  onBusy: (action: VmAction | null) => void
  /** 한 번 돈 뒤 — 된 VM 의 임대 id. 부르는 쪽이 그 고르기를 풀고 목록을 다시 읽는다. */
  onSettled: (done: ReadonlySet<string>) => void
}) {
  const me = useSession()
  // 창이 열린 순간의 조작과 대상 — 창은 그 목록을 확인받은 것이라 그 밖의 VM 에는 가지 않는다.
  const [asking, setAsking] = useState<{ chip: Chip; keys: ReadonlySet<string> } | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const allowed = vmBatchActions(chosen, me)
  const targets = asking === null ? [] : chosen.filter((row) => asking.keys.has(row.lease.id))
  // 도는 동안에는 상태가 바뀌어(종료된 VM 은 더 이상 종료를 허락하지 않는다) 창이 닫히지 않게 한다.
  const open = asking !== null && (busy !== null || (targets.length > 0 && vmBatchActions(targets, me).includes(asking.chip.action)))
  // 창을 연 사이 대상이 모두 사라졌거나 그 조작을 허락하지 않게 됐으면 창을 거둔다 —
  // 남겨 두면 다음에 무엇을 고르든 그 위에 창이 저절로 다시 선다. 오류는 거두지 않고 칩 아래로 옮겨 선다.
  if (asking !== null && !open) setAsking(null)
  // 오류는 실패한 VM 이 골라져 있는 동안만 선다 — 고르기를 풀었으면 다시 시도할 칩도 없다.
  if (failure !== null && !chosen.some((row) => failure.keys.has(row.lease.id))) setFailure(null)

  // 누른 순간의 목록이 그 조작을 모두 허락하지 않으면 아무것도 부르지 않는다. 첫 오류만 말한다.
  const run = async (chip: Chip, list: LeaseRow[], inDialog: boolean) => {
    if (list.length === 0 || !vmBatchActions(list, me).includes(chip.action)) return
    onBusy(chip.action)
    setFailure(null)
    let first: unknown = null
    const done = new Set<string>()
    const failed = new Set<string>()
    for (const row of list) {
      try {
        await call(row, chip.action)
        done.add(row.lease.id)
      } catch (err) {
        if (first === null) first = err
        failed.add(row.lease.id)
      }
    }
    onBusy(null)
    onSettled(done)
    if (first !== null) setFailure({ error: first, keys: failed })
    // 창에는 실패한 VM 만 남는다 — 같은 버튼이 곧 재시도다.
    if (inDialog) setAsking(first === null ? null : { chip, keys: failed })
  }

  const shown = CHIPS.filter((c) => allowed.includes(c.action) || busy === c.action)
  const chip = asking?.chip

  return (
    <>
      {shown.length > 0 ? (
        <div role="group" aria-label="일괄 처리" className="flex flex-wrap items-center gap-1.5">
          {shown.map((c) => (
            <button
              key={c.action}
              type="button"
              className={`${ACTION} ${c.danger ? ACTION_DANGER : ACTION_PLAIN}`}
              disabled={busy !== null}
              onClick={() => {
                if (!c.confirm) {
                  void run(c, chosen, false)
                  return
                }
                setFailure(null)
                setAsking({ chip: c, keys: new Set(chosen.map((row) => row.lease.id)) })
              }}
            >
              {c.word}
            </button>
          ))}
        </div>
      ) : null}
      {failure !== null && !open ? (
        <div className="basis-full">
          <Err error={failure.error} />
        </div>
      ) : null}
      <Dialog open={open} onOpenChange={(next) => (next || busy !== null ? undefined : setAsking(null))}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {targets.length}대 {chip?.word}
            </DialogTitle>
          </DialogHeader>
          <ul aria-label={`${chip?.word ?? ''}할 VM`} className="max-h-60 overflow-y-auto rounded-md border">
            {targets.map((row) => (
              <li key={row.lease.id} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
                <span className="truncate font-medium">{nameOf(row)}</span>
                <span className="ml-auto shrink-0 text-muted-foreground">{ownerLabel(row.lease.owner)}</span>
              </li>
            ))}
          </ul>
          {/* 반납은 되돌릴 수 없다 — 사용자 VM 상세의 반납 창과 같은 결과 한 문장이다. */}
          {chip?.action === 'end' ? <p className="text-[12px] text-muted-foreground">VM 이 정지되고 보존됩니다.</p> : null}
          <Err error={open ? failure?.error : null} />
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy !== null} onClick={() => setAsking(null)}>
              취소
            </Button>
            <Button
              type="button"
              disabled={busy !== null || targets.length === 0}
              onClick={() => chip && void run(chip, targets, true)}
              className={chip?.danger ? 'bg-danger text-white hover:bg-danger/90' : undefined}
            >
              {chip ? (busy === chip.action ? `${chip.word} 중` : chip.word) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
